// frontend/src/components/DegreeAudit/RequirementNode.tsx
// v2.3 - Simplified audit view (read-only, no drag-drop or course search)
// v2.4 - Added "Help me fulfill" button for incomplete requirements
// v2.5 - Added concentration department dropdown for CS Upper Level Concentration

import { memo, useState, useCallback, type ChangeEvent } from 'react';
import { ChevronRight, ChevronDown, Info, Sparkles, ShieldCheck, UserCheck } from 'lucide-react';

import RequirementProgressBar from './Progress/RequirementProgressBar';
import { dispatchAskChatbotHelp } from '../../utils/appEvents';
import RequirementRenderer from './RequirementDisplays/RequirementRenderer';
import type { AuditRequirementNode } from './RequirementDisplays/AuditRequirementDisplay';
import {
  useInstitutionConfig,
  formatDepartmentLabel,
} from '../../contexts/InstitutionConfigContext';

interface ConstraintResult {
  satisfied?: boolean;
  area?: string;
  type?: string;
  message?: string;
  required?: number;
  actual?: number;
  [key: string]: unknown;
}

interface AuditCourseLike {
  code?: string;
  title?: string;
  status?: string;
  [key: string]: unknown;
}

interface RequirementShape {
  type?: string;
  notes?: string[];
  courses?: AuditCourseLike[];
  progress_percentage?: number;
  courses_required?: number;
  courses_matched?: number;
  constraint_results?: ConstraintResult[];
  // W26: override-effective status, added by the backend audit pass when an
  // active override covers this requirement. Raw progress_percentage is left
  // untouched (honest dual-status); these flag the effective view.
  overridden?: boolean;
  effective_status?: string;
  // #559: a requirement the catalog leaves to the advisor, with no course list
  // the planner could check. Neither met nor unmet.
  advisor_determined?: boolean;
  [key: string]: unknown;
}

interface RequirementMetadata {
  dynamic_matches?: unknown[];
  search_filters?: Record<string, unknown>;
  course_requirements?: Record<string, { after?: unknown[] }>;
  [key: string]: unknown;
}

export interface RequirementNodeData extends AuditRequirementNode {
  name?: string;
  requirement?: RequirementShape;
  metadata?: RequirementMetadata;
  children?: RequirementNodeData[];
  requirement_code?: string;
  progress_percentage?: number;
  status?: string;
  // #559: advisor-determined requirements still outstanding under a category
  // (set only when nonzero); the effective count reflects overrides.
  advisor_determined_count?: number;
  effective_advisor_determined_count?: number;
}

const EXPANDABLE_TYPES = new Set([
  'pattern',
  'sequence',
  'dynamic_match',
  'distribution',
  'choose',
]);
const RENDERABLE_TYPES = new Set(['pattern', 'sequence', 'dynamic_match']);

function hasExpandableContent(requirement: RequirementNodeData, hasChildren: boolean): boolean {
  if (hasChildren) return true;
  const req = requirement.requirement;
  return (
    !!req &&
    ((req.courses?.length ?? 0) > 0 ||
      EXPANDABLE_TYPES.has(req.type ?? '') ||
      req.advisor_determined === true)
  );
}

function shouldRenderRequirement(requirement: RequirementNodeData): boolean {
  const req = requirement.requirement;
  if (!req) return false;
  // #559: the body explains the status and carries the override control.
  if (req.advisor_determined === true) return true;
  if ((req.courses?.length ?? 0) > 0 || RENDERABLE_TYPES.has(req.type ?? '')) return true;

  const meta = requirement.metadata;
  if (!meta) return false;
  if ((meta.dynamic_matches?.length ?? 0) > 0) return true;
  if (meta.search_filters && Object.keys(meta.search_filters).length > 0) return true;
  if (
    meta.course_requirements &&
    Object.values(meta.course_requirements).some((m) => (m?.after?.length ?? 0) > 0)
  )
    return true;
  return false;
}

/**
 * Dispatch an event to ask the chatbot for help fulfilling a requirement.
 * The ChatInterface listens for this event and sends the message.
 */
const askChatbotForHelp = (
  requirementName: string,
  programName: string | null,
  isGenEd: boolean
): void => {
  let message: string;
  if (isGenEd) {
    message = `What courses do you recommend to fulfill my "${requirementName}" General Education requirement?`;
  } else if (programName) {
    message = `What courses do you recommend to fulfill the "${requirementName}" requirement for my ${programName} degree?`;
  } else {
    message = `What courses do you recommend to fulfill my "${requirementName}" requirement?`;
  }
  dispatchAskChatbotHelp({ message, requirementName, programName, action: 'requirement_help' });
};

export interface RequirementNodeProps {
  requirement: RequirementNodeData;
  path?: string;
  expandedNodes: Record<string, boolean>;
  toggleNode: (path: string) => void;
  majorData?: unknown;
  programType?: string;
  programName?: string | null;
  isTopLevel?: boolean;
  concentrationDepartment?: string | null;
  onConcentrationChange?: ((department: string) => Promise<void> | void) | null;
}

const RequirementNode = memo(
  ({
    requirement,
    path = '0',
    expandedNodes,
    toggleNode,
    majorData = null,
    programType = 'major',
    programName = null,
    isTopLevel = false,
    concentrationDepartment = null,
    onConcentrationChange = null,
  }: RequirementNodeProps) => {
    const [updating, setUpdating] = useState(false);
    // Concentration picker options: every department at the active
    // institution with an active course. The rule it serves ("one department
    // outside the major") names no subset, so the list doesn't either.
    const {
      config: { departments },
    } = useInstitutionConfig();
    const isExpanded = expandedNodes[path];
    const hasChildren = !!requirement.children && requirement.children.length > 0;

    const isConcentration = requirement.requirement_code === 'GEN-UPPER-LEVEL-CONCENTRATION';

    const handleConcentrationChange = useCallback(
      async (e: ChangeEvent<HTMLSelectElement>): Promise<void> => {
        e.stopPropagation();
        const dept = e.target.value;
        if (!dept || !onConcentrationChange) return;
        setUpdating(true);
        try {
          await onConcentrationChange(dept);
        } finally {
          setUpdating(false);
        }
      },
      [onConcentrationChange]
    );

    const isExpandable = hasExpandableContent(requirement, hasChildren);

    const progress =
      requirement.requirement?.progress_percentage || requirement.progress_percentage || 0;
    const roundedProgress = Math.round(progress * 10) / 10;

    // W26: an active override marks this requirement effective-satisfied. The raw
    // progress bar still renders honestly; the header gets a "Met via override" badge.
    const overridden = requirement.requirement?.overridden === true;

    // #559: an advisor-determined requirement shows a badge instead of a bar
    // (the bar would read 0 of N forever). A category shows how many such
    // requirements remain under it; an override lowers the effective count.
    const advisorDetermined = requirement.requirement?.advisor_determined === true;
    const advisorCount =
      requirement.effective_advisor_determined_count ?? requirement.advisor_determined_count ?? 0;
    const nothingCheckableLeft = requirement.status === 'advisor_determined';

    const isRenderable = shouldRenderRequirement(requirement);

    return (
      <div className="border border-gray-200 rounded-lg mb-2 overflow-hidden print-requirement-node">
        <div
          className="p-2 md:p-3 cursor-pointer hover:bg-gray-50 transition-colors bg-white print:cursor-default"
          onClick={() => toggleNode(path)}
        >
          <div className="flex items-center justify-between gap-2">
            <h3
              className="font-semibold text-gray-900 text-sm md:text-base print-progress-text break-words flex-1 min-w-0"
              data-progress={roundedProgress}
            >
              {requirement.name}
            </h3>
            <span className="hidden print:inline print-progress">
              {advisorDetermined || nothingCheckableLeft
                ? '(advisor-determined)'
                : `(${roundedProgress}% complete)`}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {advisorDetermined && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-surface-sunken text-ink-2 whitespace-nowrap"
                  title="Your advisor determines how this requirement is met. The planner can't check it, so it counts neither for nor against your progress."
                >
                  <UserCheck className="w-3 h-3" />
                  <span className="hidden sm:inline">Advisor-determined</span>
                </span>
              )}
              {!advisorDetermined && advisorCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-surface-sunken text-ink-2 whitespace-nowrap"
                  title="Requirements here that your advisor determines. The progress bar covers only what the planner can check."
                >
                  <UserCheck className="w-3 h-3" />
                  <span className="hidden sm:inline">{advisorCount} advisor-determined</span>
                </span>
              )}
              {overridden && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-accent bg-opacity-10 text-accent whitespace-nowrap"
                  title="Marked satisfied via a student-recorded override; the raw progress is still shown below."
                >
                  <ShieldCheck className="w-3 h-3" />
                  <span className="hidden sm:inline">Met via override</span>
                </span>
              )}
              {isTopLevel &&
                roundedProgress < 100 &&
                !overridden &&
                !advisorDetermined &&
                !nothingCheckableLeft && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const isGenEd = programType === 'general_education';
                    askChatbotForHelp(requirement.name ?? '', programName, isGenEd);
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors whitespace-nowrap print-hide"
                  title={`Ask AI for help fulfilling "${requirement.name}"`}
                >
                  <Sparkles className="w-3 h-3" />
                  <span className="hidden sm:inline">Help</span>
                </button>
              )}
              {isExpandable &&
                (isExpanded ? (
                  <ChevronDown className="w-4 md:w-5 h-4 md:h-5 text-gray-500 print-hide" />
                ) : (
                  <ChevronRight className="w-4 md:w-5 h-4 md:h-5 text-gray-500 print-hide" />
                ))}
            </div>
          </div>

          {requirement.requirement && !advisorDetermined && (
            <div className="mt-1.5 print-hide" style={{ width: '60%' }}>
              <RequirementProgressBar requirement={requirement.requirement} showText={true} />
            </div>
          )}
        </div>

        {isConcentration && onConcentrationChange && (
          <div
            className="px-2 md:px-3 py-1.5 bg-gray-50 border-t border-gray-200 flex items-center gap-2 print-hide"
            onClick={(e) => e.stopPropagation()}
          >
            <label className="text-xs text-gray-500">Department:</label>
            <select
              value={concentrationDepartment || ''}
              onChange={handleConcentrationChange}
              disabled={updating}
              className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            >
              {/* No silent default: showing "MATH" when nothing is stored
                  claimed a choice the student never made (and a department
                  most institutions name differently). */}
              {!concentrationDepartment && (
                <option value="" disabled>
                  Choose a department
                </option>
              )}
              {concentrationDepartment &&
                !departments.some((d) => d.code === concentrationDepartment) && (
                  <option value={concentrationDepartment}>{concentrationDepartment}</option>
                )}
              {departments.map((dept) => (
                <option key={dept.code} value={dept.code}>
                  {formatDepartmentLabel(dept)}
                </option>
              ))}
            </select>
            {updating && <span className="text-xs text-gray-400">Updating...</span>}
          </div>
        )}

        {isExpandable && (
          <div
            className={`p-2 md:p-3 bg-white border-t border-gray-200 ${!isExpanded ? 'hidden print:block print-expanded' : ''}`}
          >
            {requirement.requirement?.notes && requirement.requirement.notes.length > 0 && (
              <div className="mb-3 md:mb-4 p-2 md:p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-1 md:space-x-2">
                  <Info className="w-3 md:w-4 h-3 md:h-4 text-blue-600 mt-0.5 flex-shrink-0 print-hide" />
                  <div className="text-xs md:text-sm text-blue-800">
                    {requirement.requirement.notes.map((note, idx) => (
                      <div key={idx}>{note}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {requirement.requirement?.constraint_results &&
              requirement.requirement.constraint_results.length > 0 && (
                <div className="mb-3 md:mb-4 space-y-1">
                  {requirement.requirement.constraint_results.map((constraint, idx) => {
                    const satisfied = constraint.satisfied;
                    const bgColor = satisfied
                      ? 'bg-green-50'
                      : satisfied === false
                        ? 'bg-amber-50'
                        : 'bg-gray-50';
                    const textColor = satisfied
                      ? 'text-green-700'
                      : satisfied === false
                        ? 'text-amber-700'
                        : 'text-gray-500';
                    const icon = satisfied ? '✓' : satisfied === false ? '⚠' : '•';

                    let label: string;
                    if (constraint.area) {
                      label = `At least ${constraint.required} course from ${constraint.area}`;
                      if (satisfied === false) {
                        label += ` (have ${constraint.actual})`;
                      }
                    } else if (constraint.type === 'at_least_outside_major') {
                      label =
                        constraint.message ||
                        `At least ${constraint.required} course outside major`;
                    } else {
                      label = JSON.stringify(constraint);
                    }

                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${bgColor} ${textColor}`}
                      >
                        <span className="font-bold">{icon}</span>
                        <span>{label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

            {requirement.requirement?.courses_required != null &&
              requirement.requirement.courses_required > 0 && (
                <div className="mb-2 text-xs text-gray-500">
                  Courses: {requirement.requirement.courses_matched || 0} of{' '}
                  {requirement.requirement.courses_required} required
                </div>
              )}

            {isRenderable && (
              <div className="mb-3 md:mb-4">
                <RequirementRenderer requirement={requirement} />
              </div>
            )}

            {hasChildren && requirement.children && (
              <div className="space-y-2">
                {requirement.children.map((child, index) => (
                  <RequirementNode
                    key={`${path}-${index}`}
                    requirement={child}
                    path={`${path}-${index}`}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    majorData={majorData}
                    programType={programType}
                    programName={programName}
                    concentrationDepartment={concentrationDepartment}
                    onConcentrationChange={onConcentrationChange}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

RequirementNode.displayName = 'RequirementNode';

export default RequirementNode;
